import React from 'react';
import { Alert, Descriptions, Drawer, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { MatchesTable } from './MatchesTable';
import { fmtNum, paidTodayLabel, recordLine, whenLabel, type PlayerState } from './model';

const { Text, Title } = Typography;

interface BodyProps {
  state: PlayerState;
  onOpenMatch: (id: string) => void;
  onOpenPlayer: (userId: string) => void;
}

/** What the player drawer shows for the state it is given. */
export const PlayerView: React.FC<BodyProps> = ({ state, onOpenMatch, onOpenPlayer }) => {
  const p = state.player;
  const r = p?.rating ?? null;
  return (
    <>
      {state.error && <Alert type="error" showIcon message={state.error} />}
      {state.loading && <Spin style={{ display: 'block', margin: '24px auto' }} />}
      {p && (
        <>
          <Text type="secondary" copyable={{ text: p.userId }} style={{ fontSize: 12 }}>
            {p.userId}
          </Text>
          <div style={{ margin: '8px 0 12px' }}>
            <Link to={`/players/${p.userId}`}>Open player</Link>
          </div>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Rating">
              {r == null ? 'Never played PvP' : fmtNum(r.rating, 0)}
            </Descriptions.Item>
            <Descriptions.Item label="Record">{recordLine(r)}</Descriptions.Item>
            {r && (
              <>
                <Descriptions.Item label="Balls faced">{fmtNum(r.ballsFaced, 0)}</Descriptions.Item>
                <Descriptions.Item label="Runs">{fmtNum(r.runsScored, 0)}</Descriptions.Item>
                <Descriptions.Item label="Sixes">{fmtNum(r.sixes, 0)}</Descriptions.Item>
                <Descriptions.Item label="Late reports / bad payloads">
                  {fmtNum(r.lateReports, 0)} / {fmtNum(r.badPayloads, 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Review flag" span={2}>
                  {r.flaggedAt ? (
                    <span>
                      <Tag color="red">Flagged</Tag>
                      {whenLabel(r.flaggedAt)} IST
                      {r.flagReason ? ` · ${r.flagReason}` : ''}
                    </span>
                  ) : (
                    'Not flagged'
                  )}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Paid in the last 24 h" span={2}>
              {paidTodayLabel(p.paidToday, p.paidCap)}
            </Descriptions.Item>
          </Descriptions>
          <Title level={5} style={{ margin: '20px 0 8px' }}>
            Recent matches
          </Title>
          {(p.recent ?? []).length === 0 ? (
            <Text type="secondary">No matches yet.</Text>
          ) : (
            <MatchesTable
              rows={p.recent}
              loading={false}
              onOpenMatch={onOpenMatch}
              onOpenPlayer={onOpenPlayer}
            />
          )}
        </>
      )}
    </>
  );
};

interface DrawerProps extends BodyProps {
  open: boolean;
  onClose: () => void;
}

export const PlayerDrawer: React.FC<DrawerProps> = ({ open, onClose, ...body }) => (
  <Drawer
    open={open}
    onClose={onClose}
    width={860}
    destroyOnClose
    title={body.state.player?.name ?? 'Player'}
  >
    <PlayerView {...body} />
  </Drawer>
);
